import React, { Fragment, useState, useEffect, useMemo } from "react";
import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import { KPI } from "../../components/KPI.js";
import InsightsTable from "./InsightsTable.jsx";
import { useQuery } from "@tanstack/react-query";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useKeycloak } from "@react-keycloak/web";
import { useFilterStoreWithMomentDates } from "../../store/filterStore.js";


const API_URL = process.env.REACT_APP_API_URL;

const InsightsOverview = () => {
	const [totalPossibleSavings, setTotalPossibleSavings] = useState(0);
	const [totalInsights, setTotalInsights] = useState(0);
	const { keycloak } = useKeycloak();
	// Use Zustand store for date state with moment dates
	// Nothing is read from this any more, but the call is kept so the subscription
	// and the hook order stay exactly as they were.
	useFilterStoreWithMomentDates();


	const retrieveInsights = async(token) => {
		if (token === "") return;

		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + token);

		let method = "GET";

		let requestOptions = {
			method: method,
			headers: headers,
		};

		const response = await fetch(API_URL + "/get-insights", requestOptions);

		if (!response.ok) {
			throw new Error("Failed to fetch data");
		}

		return response.json();
	}

	const statusInsights = useQuery(
		["insights"],
		() => retrieveInsights(keycloak.token),
		{
			enabled: keycloak.token !== "",
			staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh for 5 minutes
			refetchOnWindowFocus: false, // Prevent refetching when window regains focus
			cacheTime: 10 * 60 * 1000, // Keep data in cache for 10 minutes
		}
	);

	// Get insights data from query result
	const insights = useMemo(() => statusInsights.data?.Insights || [], [statusInsights.data]);

	// Calculate total possible savings from insights data
	useEffect(() => {
		if (insights && Array.isArray(insights)) {
			const totalSavings = insights.reduce((sum, insight) => {
				return sum + (insight.costs || 0);
			}, 0);
			setTotalPossibleSavings(totalSavings);
			setTotalInsights(insights.length);
		}
	}, [insights]);

	return (
		<Fragment>
            <AdministrationHeader
				title="Insights"
                subTitle="Define and manage the tags that are used in the different cloud environments"
				className="text-text-3"
				icon={icon({name: "lightbulb-on", style: "regular"})}
			/>
			<div className="text-left flex flex-auto flex-col ml-10 mt-4 border-solid border-border">
				<div className="flex flex-row gap-4">
					<div className="flex-shrink-0">
						<KPI
							title="Total Possible Savings"
							currentValue={totalPossibleSavings}
							format="currency"
							showChange={false}
						/>
					</div>
					<div className="flex-shrink-0">
						<KPI
							title="Insights"
							currentValue={totalInsights}
							format="decimal"
							showChange={false}
						/>
					</div>
				</div>

				<div className="pt-4 pb-2 my-2 rounded-lg h-screen flex flex-auto flex-col">
					<InsightsTable
						data={insights}
					/>
				</div>
			</div>
		</Fragment>
	);
};

export default InsightsOverview;
